import React from "react";
import { App, Integration as IntegrationType } from "/app/src/models";
import { Route, Routes, useLocation } from "react-router-dom";
import { useNumberId } from "/app/src/hooks/useNumberId";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { integrationService } from "/app/src/services";
import { Helmet } from "react-helmet";
import { Row, Col } from "antd";
import { Tabs, Tab } from "@heroui/react";
import Settings from "./settings";
import OrderProcessorOrders from "./orders";
import OrderProcessorOrder from "./orders/order";
import ManualOrderProcessorOrder from "./orders/order/manual";

/**
 * Renders an integration component based on the provided appId and integration details.
 * @param {object} props - The properties object.
 * @param {number} props.appId - The ID of the app.
 * @param {IntegrationType} props.integration - The integration object containing integration details.
 * @returns {JSX.Element} The rendered integration component.
 */
export default function Integration({ app }: { app: App }) {
  const { integrationId } = useNumberId();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  // Get integration
  const { data: integration, isFetching } = useQuery({
    queryKey: ["integration", integrationId],
    queryFn: () => {
      return integrationService.getSingle(integrationId);
    },
    enabled: Boolean(integrationId),
    initialData: { integration: {} },
    select: (data: { integration: IntegrationType }) => {
      return data.integration;
    },
  });

  const links = [
    {
      path: `/apps/${app.id}/integrations/${integration.id}`,
      key: "settings",
    },
    {
      path: `/apps/${app.id}/integrations/${integration.id}/orders`,
      key: "orders",
    },
  ];

  /**
   * Determines the active tab key based on the current pathname.
   *
   * @returns {string} The active tab path.
   */
  const getActiveTabKey = () => {
    const baseOrdersPath = `/apps/${app.id}/integrations/${integration.id}/orders`;
    if (
      pathname === baseOrdersPath ||
      pathname.startsWith(`${baseOrdersPath}/`)
    ) {
      return links.find((link) => link.key === "orders").path;
    }
    return pathname;
  };

  return (
    <div className="integration">
      <Helmet>
        <title>{integration.name}</title>
      </Helmet>

      {!isFetching && (
        <Row gutter={24}>
          <Col span={24}>
            <Tabs
              selectedKey={getActiveTabKey()}
              color="primary"
              classNames={{
                tab: "bg-white px-4 py-2",
                tabList: "bg-white flex",
              }}
              className="pb-[20px]"
            >
              {links.map((item) => (
                <Tab
                  key={item.path}
                  id={item.key}
                  title={t(`translation:${item.key}`, item.key)}
                  href={item.path}
                />
              ))}
            </Tabs>

            <Routes>
              <Route
                path="/"
                element={<Settings integration={integration} />}
              />
              <Route
                path="orders"
                element={<OrderProcessorOrders integration={integration} />}
              />
              {integration?.number === "automatic" ? (
                <Route
                  path="orders/:orderId/*"
                  element={<OrderProcessorOrder />}
                />
              ) : (
                <Route
                  path="orders/:orderId/*"
                  element={
                    <ManualOrderProcessorOrder integration={integration} />
                  }
                />
              )}
            </Routes>
          </Col>
        </Row>
      )}
    </div>
  );
}
